import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Printer, Mail, ArrowLeft, Loader2, Send, FileDown } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { APP_VERSION } from "@/lib/version";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { tr } from "@/lib/i18n";

const fmt = (n) => `$${(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2 })}`;
const dstr = (d) => (d ? new Date(d).toLocaleDateString(document.documentElement.lang === "fr" ? "fr-CA" : "en-CA", { year: "numeric", month: "long", day: "numeric" }) : "—");

function narrate(app) {
    const s = app.statistics || {};
    const paid = (app.fees || []).filter((f) => f.status === "Paid").reduce((x, f) => x + f.amount, 0);
    const owing = (app.fees || []).filter((f) => f.status !== "Paid").reduce((x, f) => x + f.amount, 0);
    const done = (app.events || []).filter((e) => e.status === "completed").length;
    const received = (app.circulations || []).filter((c) => ["received", "closed"].includes(c.status)).length;
    const out = [];
    out.push(`${app.title} (${app.file_number}) is a ${app.type} application for lands at ${app.address}, ${app.ward}, currently ${app.status}. The proposal covers ${s.area_ha ?? "—"} ha${s.units ? ` with ${s.units} proposed residential units at a density of ${s.density_uph} units/ha` : ""}. The file was submitted on ${dstr(app.submitted_date)} and is led by ${app.planner_name}.`);
    out.push(`Of ${(app.events || []).length} tracked milestones, ${done} are complete. ${received} of ${(app.circulations || []).length} circulated agencies have responded. ${received < (app.circulations || []).length ? tr("Outstanding agency responses remain the primary gating item before the file can advance to a recommendation report.") : tr("All circulated agencies have responded.")}`);
    out.push(`Financially, ${fmt(paid)} has been collected against ${fmt(paid + owing)} in assessed fees${owing > 0 ? `, leaving ${fmt(owing)} outstanding` : tr(" — the file is fully paid up")}.`);
    if ((app.appeals || []).length > 0) {
        out.push(`The file is subject to ${app.appeals.length} appeal${app.appeals.length > 1 ? "s" : ""}, most recently ${app.appeals[app.appeals.length - 1].appeal_type} by ${app.appeals[app.appeals.length - 1].appellant} (${app.appeals[app.appeals.length - 1].status}).`);
    }
    return out;
}

export default function PrintReport() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [app, setApp] = useState(null);
    const [showShare, setShowShare] = useState(false);
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");
    const [sending, setSending] = useState(false);
    useLanguage();

    useEffect(() => {
        api.get(`/applications/${id}`).then((r) => setApp(r.data)).catch(() => {});
        // eslint-disable-next-line
    }, [id]);

    const share = async () => {
        setSending(true);
        try {
            const { data } = await api.post("/reports/share", { application_id: app.id, email, message });
            toast.success(data.note || "Report shared");
            setShowShare(false);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSending(false);
        }
        window.location.href = `mailto:${email}?subject=${encodeURIComponent(`Application Summary — ${app.file_number} ${app.title}`)}&body=${encodeURIComponent(`${message}\n\nView the file: ${window.location.origin}/portal (search ${app.file_number})`)}`;
    };

    const downloadPdf = async () => {
        try {
            const res = await api.get(`/applications/${id}/report.pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            window.open(url, "_blank");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    if (!app) return <div className="min-h-screen flex items-center justify-center text-slate-400 text-sm">{tr("Preparing report…")}</div>;

    const narration = narrate(app);

    return (
        <div className="min-h-screen bg-slate-200 py-8 print:bg-white print:py-0" data-testid="print-report-page">
            <div className="max-w-3xl mx-auto px-4 mb-4 flex items-center justify-between print:hidden">
                <button data-testid="report-back-btn" onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0092B0] transition-colors">
                    <ArrowLeft className="w-4 h-4" /> {tr("Back to file")}
                </button>
                <div className="flex gap-2">
                    <button data-testid="report-share-btn" onClick={() => setShowShare(true)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[10px] text-sm font-bold border border-slate-300 bg-white hover:border-[#00B4D8] hover:text-[#0092B0] transition-colors">
                        <Mail className="w-4 h-4" /> {tr("Share via email")}
                    </button>
                    <button data-testid="report-download-pdf-btn" onClick={downloadPdf} className="brand-btn inline-flex items-center gap-2 px-4 py-2.5 rounded-[10px] text-sm font-bold">
                        <FileDown className="w-4 h-4" /> {tr("Download PDF")}
                    </button>
                    <button data-testid="report-print-btn" onClick={() => window.print()} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[10px] text-sm font-bold border border-slate-300 bg-white hover:border-[#00B4D8] hover:text-[#0092B0] transition-colors">
                        <Printer className="w-4 h-4" /> {tr("Print / Save PDF")}
                    </button>
                </div>
            </div>

            <div className="print-page max-w-3xl mx-auto bg-white shadow-xl rounded-[8px] overflow-hidden print:rounded-none">
                <div className="bg-[#0A2540] text-white px-10 py-8 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[#7CD4EA] font-bold">{tr("City of Brampton · Planning Services")}</div>
                        <h1 className="font-heading font-bold text-2xl mt-2">{tr("Application Summary Report")}</h1>
                        <div className="text-sm text-slate-300 mt-1">{app.file_number} · {app.type}</div>
                    </div>
                    <img src="/assets/logo-light.png" alt="LandLogic NexusOne" className="h-16 w-auto" />
                </div>

                <div className="px-10 py-8 space-y-8">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                            <h2 className="font-heading font-bold text-xl text-[#0A2540]">{app.title}</h2>
                            <div className="text-sm text-slate-500 mt-1">{app.address} · {app.ward}</div>
                        </div>
                        <StatusBadge status={app.status} size="md" />
                    </div>

                    <section data-testid="report-narration" className="rounded-[10px] bg-slate-50 border border-slate-200 p-5">
                        <h3 className="text-xs uppercase tracking-[0.18em] font-bold text-[#0092B0] mb-3">{tr("Planner's narrative")}</h3>
                        {narration.map((p, i) => <p key={i} className="text-sm text-slate-700 leading-relaxed mb-2.5 last:mb-0">{p}</p>)}
                    </section>

                    <section>
                        <h3 className="text-xs uppercase tracking-[0.18em] font-bold text-slate-400 mb-3">{tr("Key facts")}</h3>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-slate-200 rounded-[10px] overflow-hidden border border-slate-200">
                            {[["Applicant", app.applicant_name], ["Lead planner", app.planner_name], ["Submitted", dstr(app.submitted_date)],
                              ["Units", app.statistics?.units ?? "—"], ["Site area", `${app.statistics?.area_ha ?? "—"} ha`], ["Density", app.statistics?.density_uph ? `${app.statistics.density_uph} uph` : "—"],
                              ["Legal description", app.legal_description || "—"], ["Ward", app.ward], ["Category", app.category]].map(([l, v]) => (
                                <div key={l} className="bg-white px-4 py-3">
                                    <div className="text-[10px] uppercase tracking-[0.12em] text-slate-400 font-bold">{l}</div>
                                    <div className="text-sm font-semibold text-[#0A2540] mt-0.5 break-words">{v}</div>
                                </div>
                            ))}
                        </div>
                    </section>

                    <section>
                        <h3 className="text-xs uppercase tracking-[0.18em] font-bold text-slate-400 mb-3">{tr("Milestones")}</h3>
                        <table className="w-full text-sm">
                            <tbody>
                                {(app.events || []).map((e) => (
                                    <tr key={e.id} className="border-b border-slate-100">
                                        <td className="py-2.5 font-medium text-[#0A2540]">{e.name}</td>
                                        <td className="py-2.5 text-slate-500 font-data text-xs">{dstr(e.date)}</td>
                                        <td className="py-2.5 text-right"><span className={`text-[11px] font-bold px-2 py-0.5 rounded-full capitalize ${e.status === "completed" ? "bg-[#06D6A0]/12 text-[#05A37E]" : e.status === "current" ? "bg-[#FFB703]/15 text-[#B8860B]" : "bg-slate-100 text-slate-500"}`}>{e.status}</span></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>

                    <section>
                        <h3 className="text-xs uppercase tracking-[0.18em] font-bold text-slate-400 mb-3">Circulation positions ({(app.circulations || []).length})</h3>
                        <table className="w-full text-sm">
                            <tbody>
                                {(app.circulations || []).map((c) => (
                                    <tr key={c.id} className="border-b border-slate-100 align-top">
                                        <td className="py-2.5 font-medium text-[#0A2540] w-1/3">{c.recipient}</td>
                                        <td className="py-2.5 text-xs text-slate-500">{c.comments || "—"}</td>
                                        <td className="py-2.5 text-right w-24"><span className="text-[11px] font-bold capitalize text-slate-600">{c.status}</span></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>

                    <section className="grid sm:grid-cols-2 gap-8">
                        <div>
                            <h3 className="text-xs uppercase tracking-[0.18em] font-bold text-slate-400 mb-3">{tr("Fees")}</h3>
                            <table className="w-full text-sm">
                                <tbody>
                                    {(app.fees || []).map((f) => (
                                        <tr key={f.id} className="border-b border-slate-100">
                                            <td className="py-2 text-[#0A2540]">{f.description}</td>
                                            <td className="py-2 text-right font-data text-xs">{fmt(f.amount)}</td>
                                            <td className="py-2 text-right w-16"><span className={`text-[11px] font-bold ${f.status === "Paid" ? "text-[#05A37E]" : "text-[#B8860B]"}`}>{f.status}</span></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {(app.appeals || []).length > 0 && (
                            <div>
                                <h3 className="text-xs uppercase tracking-[0.18em] font-bold text-slate-400 mb-3">{tr("Appeals")}</h3>
                                {(app.appeals || []).map((a) => (
                                    <div key={a.id} className="text-sm border-b border-slate-100 py-2">
                                        <span className="font-medium text-[#0A2540]">{a.appeal_type}</span> — {a.appellant}
                                        <span className="text-xs text-slate-500"> · {a.status} · filed {a.date_filed}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>

                    <div className="pt-6 border-t border-slate-200 flex items-center justify-between text-[10px] text-slate-400 font-data">
                        <span>Generated {dstr(new Date().toISOString())} · LandLogic NexusOne {APP_VERSION}</span>
                        <span>{tr("City of Brampton — confidential working document")}</span>
                    </div>
                </div>
            </div>

            <Dialog open={showShare} onOpenChange={setShowShare}>
                <DialogContent className="sm:max-w-md rounded-[16px]" data-testid="share-dialog">
                    <DialogHeader><DialogTitle className="font-heading">{tr("Share report via email")}</DialogTitle></DialogHeader>
                    <div className="space-y-4 mt-2">
                        <input data-testid="share-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                            placeholder="recipient@example.ca"
                            className="w-full rounded-[8px] border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#00B4D8]" />
                        <textarea data-testid="share-message-input" rows={3} value={message} onChange={(e) => setMessage(e.target.value)}
                            placeholder={tr("Optional note to include…")}
                            className="w-full rounded-[8px] border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#00B4D8]" />
                        <p className="text-[11px] text-slate-400 leading-relaxed">{tr("Opens your email client with the report link prefilled, and logs the share to the audit trail. Connect a live email provider under Admin → Integrations for direct delivery.")}</p>
                        <button data-testid="share-send-btn" onClick={share} disabled={!email || sending}
                            className="brand-btn w-full py-3 rounded-[10px] font-bold text-sm disabled:opacity-40 inline-flex items-center justify-center gap-2">
                            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Share report
                        </button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
